import React from "react"
import { css, type Theme } from "@emotion/react"
import { Link, NavLink } from "react-router"
import { type IconColor } from "@myvp/shared/src/styles/icon-utils"

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive"

export interface CoreButtonProps {
  className?: string
  isAnchorOpen?: boolean
  variant: ButtonVariant
  height: "small" | "medium" | "large"
}

const DisabledLink = (
  allProps:
    | React.ComponentProps<typeof NavLink>
    | React.ComponentProps<typeof Link>
    | React.ComponentProps<"a">
) => {
  const {
    // props that have different types when a `NavLink` is used (can be functions)
    children,
    className,
    style,
    // `NavLink` specific props
    caseSensitive,
    end,
    // `Link` specific props
    reloadDocument,
    replace,
    state,
    preventScrollReset,
    relative,
    to,
    ...props
  } = {
    // avoid TS errors from unpacking props that don't exist for Link or 'a' components.
    ...(undefined as unknown as Record<
      Exclude<
        | keyof React.ComponentProps<typeof NavLink>
        | keyof React.ComponentProps<typeof Link>,
        keyof React.ComponentProps<"a">
      >,
      undefined
    >),
    ...allProps,
  }
  // NOTE: it doesn't seem possible to detect `isActive` in all cases based on the NavLink
  // internal source code (it uses `encodeLocation` from a private context). It could probably
  // be implemented here to handle most cases, but we probably don't have this use case (YAGNI).
  // github source perma link for v6: https://github.com/remix-run/react-router/blob/d9ed8241d677de006a9bfe808d32fe4582184dad/packages/react-router-dom/index.tsx#L1083-L1129
  const navLinkStatus = {
    isActive: false,
    isPending: false,
    isTransitioning: false,
  }
  // html based on recommendations from:
  // - https://www.scottohara.me/blog/2021/05/28/disabled-links.html
  // - https://w3c.github.io/html-aria/#el-a
  return (
    // oxlint-disable-next-line jsx_a11y/anchor-is-valid
    <a
      {...props}
      // omit href prop so clicking it doesn't do anything AND right click doesn't show 'Open in New Tab"
      href={undefined}
      // explicit role="link" because omitting `href` removes the implicit role="link"
      // also, allow other components to override this role explicitly
      role={props.role || "link"}
      aria-disabled="true"
      // function cases are for handling `NavLink` props
      className={
        typeof className === "function" ? className(navLinkStatus) : className
      }
      style={typeof style === "function" ? style(navLinkStatus) : style}
    >
      {typeof children === "function" ? children(navLinkStatus) : children}
    </a>
  )
}

export const InternalNavLink = (
  props: React.ComponentProps<typeof NavLink>
) => {
  const Component =
    props["aria-disabled"] === true || props["aria-disabled"] === "true"
      ? DisabledLink
      : NavLink
  return (
    <Component {...props} ref={props.ref}>
      {props.children}
    </Component>
  )
}

export const InternalAnchor = (props: React.ComponentProps<"a">) => {
  if (props["aria-disabled"] === true || props["aria-disabled"] === "true") {
    return (
      <DisabledLink {...props} ref={props.ref}>
        {props.children}
      </DisabledLink>
    )
  }

  return (
    <a {...props} ref={props.ref}>
      {props.children}
    </a>
  )
}

export const InternalLink = (props: React.ComponentProps<typeof Link>) => {
  const Component =
    props["aria-disabled"] === true || props["aria-disabled"] === "true"
      ? DisabledLink
      : Link
  return <Component {...props}>{props.children}</Component>
}

export const InternalButton = (props: React.ComponentProps<"button">) => (
  <button
    {...props}
    onClick={(event) => {
      if (event.currentTarget.ariaDisabled === "true") {
        event.preventDefault()
      } else {
        props.onClick?.(event)
      }
    }}
  >
    {props.children}
  </button>
)

export const INPUT_BUTTON_TEXT_COLORS = ["default", "link", "inverse"] as const
export type InputButtonTextColors = (typeof INPUT_BUTTON_TEXT_COLORS)[number]

export const getButtonIconColor = (args: {
  variant: ButtonVariant
  iconColor?: IconColor // Only for tertiary variant
  disabled?: boolean
}): IconColor => {
  switch (args.variant) {
    case "primary":
      return "inverse"
    case "secondary":
      return args.disabled ? "disabled" : "default"
    case "tertiary":
      if (args.disabled) {
        return "disabled"
      } else if (args.iconColor) {
        return args.iconColor
      } else {
        return "link"
      }
    case "destructive":
      return args.disabled ? "disabled" : "critical"
  }
}

export const heightStylesPx = {
  xsmall: 24,
  small: 32,
  medium: 40,
  large: 44,
}
/** `xsmall` is for icon buttons only */
export const heightStyles = {
  xsmall: css`
    min-height: ${heightStylesPx.xsmall}px;
  `,
  small: css`
    min-height: ${heightStylesPx.small}px;
  `,
  medium: css`
    min-height: ${heightStylesPx.medium}px;
  `,
  large: css`
    min-height: ${heightStylesPx.large}px;
  `,
}

export const activeCss = (forceActive: boolean) => {
  if (forceActive) {
    return css`
      background-color: var(--activeColor);
    `
  }
}

const secondary = (theme: Theme) => css`
  --activeColor: ${theme.palette.surface.neutral.pressed};
  background-color: ${theme.palette.surface.neutral.default};
  border: 1px solid ${theme.palette.border.neutral.default};
  // Padding reduced to account for border
  padding: 3px 4px;
  color: ${theme.palette.content.neutral.default};

  :hover {
    background-color: ${theme.palette.surface.neutral.hover};
  }

  :active,
  &[aria-pressed="true"] {
    background-color: var(--activeColor);
  }

  :disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    background-color: ${theme.palette.surface.neutral.disabled};
    color: ${theme.palette.content.neutral.disabled};
    border: none;
  }
`
export const variantStyles = {
  primary: (theme: Theme) => css`
    --activeColor: ${theme.palette.surface.primary.pressed};
    background-color: ${theme.palette.surface.primary.strong};
    border: none;
    color: ${theme.palette.content.neutral.inverse};

    :hover {
      background-color: ${theme.palette.surface.primary.hover};
    }

    :active,
    &[aria-pressed="true"] {
      background-color: var(--activeColor);
    }

    :disabled,
    &[aria-disabled="true"] {
      cursor: not-allowed;
      background-color: ${theme.palette.surface.primary.disabled};
    }
  `,
  secondary,
  destructive: [
    secondary,
    (theme: Theme) => css`
      color: ${theme.palette.content.critical.default};
    `,
  ],
  tertiary: (theme: Theme) => css`
    --activeColor: ${theme.palette.surface.neutral.pressed};
    color: ${theme.palette.content.primary.default};
    border: none;
    background: none;

    :hover {
      background-color: ${theme.palette.surface.neutral.hover};
    }

    :active,
    &[aria-pressed="true"] {
      background-color: var(--activeColor);
    }

    :disabled,
    &[aria-disabled="true"] {
      cursor: not-allowed;
      color: ${theme.palette.content.neutral.disabled};
      background-color: unset;
    }
  `,
}

export const vaultHeaderButtonCss = (theme: Theme) => css`
  :hover {
    background-color: ${theme.palette.surface.vault.hover};
  }
  :active {
    background-color: ${theme.palette.surface.vault.pressed};
  }
`

export const vaultHeaderOpenCss = (theme: Theme) => css`
  background-color: ${theme.palette.surface.vault.selected};
`

export const darkHeaderButtonCss = (theme: Theme) => css`
  :hover {
    background-color: ${theme.palette.surface.tooltip.hover};
  }
  :active {
    background-color: ${theme.palette.surface.tooltip.pressed};
  }
`
